import { useEffect, useState } from "react";
import { api, formatPrice } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";

export default function AdminExceptions() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState({});

  const load = () =>
    api.get("/admin/exceptions").then(({ data }) => setOrders(data.orders)).finally(() => setLoading(false));
  useEffect(load, []);

  const act = async (id, kind) => {
    try {
      await api.post(`/admin/orders/${id}/${kind}`, { note: notes[id] || "" });
      toast.success(kind === "approve" ? "Order approved & forwarded" : "Order rejected");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  return (
    <div className="container-tight py-8" data-testid="admin-exceptions-page">
      <AdminNav />
      <AdminHeader title="HUMAN REVIEW QUEUE" subtitle="Orders that require manual review before fulfillment. Never auto-cancelled." />

      {loading ? <div className="font-mono text-zinc-500">LOADING...</div> :
       orders.length === 0 ? (
         <div className="border border-zinc-800 bg-[#0a0a0c] p-16 rounded-sm text-center">
           <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-4" />
           <div className="font-display font-black text-xl mb-1">QUEUE CLEAR</div>
           <div className="text-sm text-zinc-500">No orders require human review.</div>
         </div>
       ) : (
         <div className="space-y-4">
           {orders.map((o) => (
             <div key={o.id} data-testid={`exception-${o.id}`} className="border border-yellow-500/30 bg-[#0a0a0c] rounded-sm">
               <div className="flex flex-wrap items-center gap-4 px-4 py-3 bg-yellow-500/5 border-b border-yellow-500/20">
                 <AlertTriangle className="w-5 h-5 text-yellow-400" />
                 <div>
                   <div className="font-mono text-[10px] text-zinc-500 tracking-widest">ORDER</div>
                   <div className="font-mono text-xs text-white">{o.id.slice(0, 8).toUpperCase()}</div>
                 </div>
                 <div>
                   <div className="font-mono text-[10px] text-zinc-500 tracking-widest">CUSTOMER</div>
                   <div className="font-mono text-xs text-white">{o.user_email}</div>
                 </div>
                 <div>
                   <div className="font-mono text-[10px] text-zinc-500 tracking-widest">TOTAL</div>
                   <div className="font-mono text-xs font-bold text-white">{formatPrice(o.total)}</div>
                 </div>
                 <div>
                   <div className="font-mono text-[10px] text-zinc-500 tracking-widest">EST. PROFIT</div>
                   <div className="font-mono text-xs text-green-400">{o.contribution_profit_estimate != null ? formatPrice(o.contribution_profit_estimate) : "—"}</div>
                 </div>
                 <div className="ml-auto font-mono text-xs text-yellow-400 uppercase tracking-widest">{o.state}</div>
               </div>
               <div className="p-4 grid md:grid-cols-2 gap-4">
                 <div>
                   <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Review Reasons</div>
                   <ul className="space-y-1">
                     {(o.review_reasons || []).map((r, i) => (
                       <li key={i} className="font-mono text-xs text-red-400">• {r}</li>
                     ))}
                   </ul>
                 </div>
                 <div>
                   <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase mb-2">Items</div>
                   {o.items.map((it) => (
                     <div key={it.product_id} className="flex items-center gap-2 text-xs mb-1">
                       <img src={it.image} alt="" className="w-8 h-8 object-cover border border-zinc-800" />
                       <div className="truncate flex-1">{it.name}</div>
                       <span className="font-mono text-zinc-500">×{it.quantity}</span>
                     </div>
                   ))}
                 </div>
               </div>
               <div className="border-t border-zinc-800 p-4 flex flex-wrap items-center gap-3">
                 <Input data-testid={`exc-note-${o.id}`} placeholder="Review note (optional)"
                   value={notes[o.id] || ""} onChange={(e) => setNotes({ ...notes, [o.id]: e.target.value })}
                   className="flex-1 min-w-[200px] bg-[#050505] border-zinc-800 rounded-sm" />
                 <Button data-testid={`approve-${o.id}`} onClick={() => act(o.id, "approve")}
                   className="bg-green-500 hover:bg-green-600 text-black rounded-sm font-mono tracking-wider">
                   <CheckCircle2 className="w-4 h-4 mr-2" />APPROVE
                 </Button>
                 <Button data-testid={`reject-${o.id}`} onClick={() => act(o.id, "reject")} variant="outline"
                   className="border-red-500/50 text-red-400 hover:bg-red-500/10 bg-transparent rounded-sm font-mono tracking-wider">
                   <XCircle className="w-4 h-4 mr-2" />REJECT
                 </Button>
               </div>
             </div>
           ))}
         </div>
       )}
    </div>
  );
}
